import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  StreamingClientConfigDataService,
  StreamingCollectionPageDataService,
  StreamingDetailPageDataService,
  StreamingGenrePageDataService,
  StreamingMenuDataService,
  StreamingPageDataService,
  StreamingRedeemPageDataService,
  StreamingRecommendationTrayDataService,
} from '@cxb/content-stb-api';
import { PlatformApiModel, ProfileTypeApiModel, UserSegmentApiModel } from '@cxb/content-stb-api/model/models';
import { CreateModel, Entity } from '@nx-data/nx-data';
import { NgxDatabase, NgxTable } from '@nx-data/nxg-database';
import { Observable, tap } from 'rxjs';
import { takeFirst } from '../lib/utils';
import { ClientConfigApiEntity } from './entities/client-config-api-entity';
import { CollectionPageEntity } from './entities/collection-page-entity';
import { GenrePageEntity } from './entities/genre-page-entity';
import { MarketingDetailPageItemEntity } from './entities/marketing-detail-page-item-entity';
import { MenuEntity } from './entities/menu-entity';
import { PageEntity } from './entities/page-entity';
import { PageItemEntity } from './entities/page-item-entity';
import { RecoTrayContentEntity } from './entities/reco-tray-content-entity';
import { RedeemPageEntity } from './entities/redeem-page-entity';
import { SellableSubscriptionsEntity } from './entities/sellable-subscriptions-entity';
import { SimilarTitlesEntity } from './entities/similar-titles-entity';

/* eslint-disable rxjs/finnish */

@Injectable({ providedIn: 'root' })
export class EntityDataLoaderService {
  private readonly tables = {
    clientConfig: this.database.getTable(ClientConfigApiEntity),
    similarTitles: this.database.getTable(SimilarTitlesEntity),
    reco: this.database.getTable(RecoTrayContentEntity),
    genrePage: this.database.getTable(GenrePageEntity),
    pageItem: this.database.getTable(PageItemEntity),
    detailPageItem: this.database.getTable(MarketingDetailPageItemEntity),
    page: this.database.getTable(PageEntity),
    collectionPage: this.database.getTable(CollectionPageEntity),
    menu: this.database.getTable(MenuEntity),
    redeemPage: this.database.getTable(RedeemPageEntity),
    sellableSubscriptions: this.database.getTable(SellableSubscriptionsEntity),
  };

  constructor(
    private readonly database: NgxDatabase,
    private readonly clientConfigDataService: StreamingClientConfigDataService,
    private readonly recommendationDataService: StreamingRecommendationTrayDataService,
    private readonly genrePageDataService: StreamingGenrePageDataService,
    private readonly detailPageDataService: StreamingDetailPageDataService,
    private readonly pageDataService: StreamingPageDataService,
    private readonly collectionPageDataService: StreamingCollectionPageDataService,
    private readonly menuDataService: StreamingMenuDataService,
    private readonly redeemPageDataService: StreamingRedeemPageDataService,
    private readonly httpClient: HttpClient
  ) {}

  private get<T extends Entity>(id: string, table: NgxTable<T>, fallbackObservable: Observable<T>): Observable<T> {
    if (table.hasLive(id)) {
      return table.get$(id).pipe(takeFirst()) as Observable<T>;
    } else {
    }

    return fallbackObservable.pipe(
      tap((result) => {
        table.insertMultiple(
          (result instanceof Array ? result : [result]).map((it) => {
            const inserted = {};
            Object.assign(inserted, it);
            inserted['id'] = id;
            return inserted as CreateModel<T>;
          })
        );
      })
    );
  }

  public getClientConfiguration(platform: PlatformApiModel): Observable<ClientConfigApiEntity> {
    const id = ClientConfigApiEntity.singletonID;

    // @ts-ignore
    return this.get(id, this.tables.clientConfig, this.clientConfigDataService.getStreamingClientConfiguration(platform));
  }

  public getSimilarProgramAvailabilitiesV2(programAvailabilityGuid: string, sessionId: string): Observable<SimilarTitlesEntity> {
    const id = programAvailabilityGuid;

    // @ts-ignore
    return this.get(
      id,
      this.tables.similarTitles,
      // @ts-ignore
      this.recommendationDataService.getSimilarProgramAvailabilitiesV2(programAvailabilityGuid, sessionId)
    );
  }

  public getRecommendationTrayContent(
    sessionId: string,
    countryCode: string,
    additionalProperties: {
      [key: string]: string;
    }
  ): Observable<RecoTrayContentEntity> {
    const props = Object.keys(additionalProperties)
      .sort()
      .map((key) => `${key}-${additionalProperties[key]};`);
    const id = `${countryCode}:${props}`;

    // @ts-ignore
    return this.get(
      id,
      this.tables.reco,
      // @ts-ignore
      this.recommendationDataService.getRecommendationTrayContent(sessionId, countryCode, additionalProperties)
    );
  }

  public genrePage(
    genre: string,
    platform: PlatformApiModel,
    userSegment: UserSegmentApiModel,
    countryCode: string
  ): Observable<GenrePageEntity> {
    const id = `${countryCode}:${platform}:${genre}`;

    // @ts-ignore
    return this.get(id, this.tables.genrePage, this.genrePageDataService.genrePage(genre, platform, userSegment, countryCode));
  }

  public pageItemV4(slug: string, platform: PlatformApiModel): Observable<PageItemEntity> {
    // @ts-ignore
    return this.get(slug, this.tables.pageItem, this.pageDataService.pageItemV4(slug, platform));
  }

  public marketingMovieDetailPageV2(
    contentGuid: string,
    platform: PlatformApiModel,
    userSegment: UserSegmentApiModel,
    countryCode: string
  ): Observable<MarketingDetailPageItemEntity> {
    const id = `movie${contentGuid}:${countryCode}`;

    // @ts-ignore
    return this.get(
      id,
      this.tables.detailPageItem,
      // @ts-ignore
      this.detailPageDataService.marketingMovieDetailPageV2(contentGuid, platform, userSegment, countryCode)
    );
  }

  public pageV4(slug: string, platform: PlatformApiModel, userSegment: UserSegmentApiModel, countryCode: string): Observable<PageEntity> {
    const id = `${slug}:${countryCode}`;
    // @ts-ignore
    return this.get(id, this.tables.page, this.pageDataService.pageV4(slug, platform, userSegment, countryCode));
  }

  public collectionPageV1(
    slug: string,
    platform: PlatformApiModel,
    userSegment: UserSegmentApiModel,
    countryCode: string
  ): Observable<CollectionPageEntity> {
    const id = `${slug}:${countryCode}`;

    // @ts-ignore
    return this.get(
      id,
      this.tables.collectionPage,
      // @ts-ignore
      this.collectionPageDataService.collectionPageV1(slug, platform, userSegment, countryCode)
    );
  }

  public getMenu(platform: PlatformApiModel, profileType: ProfileTypeApiModel): Observable<MenuEntity> {
    const id = profileType as string;
    // @ts-ignore
    return this.get(id, this.tables.menu, this.menuDataService.getMenu(platform, profileType));
  }

  public marketingGenericRedeemPageV1(
    platform: PlatformApiModel,
    userSegment: UserSegmentApiModel,
    countryCode: string
  ): Observable<RedeemPageEntity> {
    const id = countryCode;
    // @ts-ignore
    return this.get(
      id,
      this.tables.redeemPage,
      // @ts-ignore
      this.redeemPageDataService.marketingGenericRedeemPageV1(platform, userSegment, countryCode)
    );
  }

  public marketingPartnerRedeemPageV1(
    slug: string,
    platform: PlatformApiModel,
    userSegment: UserSegmentApiModel,
    countryCode: string
  ): Observable<RedeemPageEntity> {
    const id = `${countryCode}:${slug}`;
    // @ts-ignore
    return this.get(
      id,
      this.tables.redeemPage,
      // @ts-ignore
      this.redeemPageDataService.marketingPartnerRedeemPageV1(slug, platform, userSegment, countryCode)
    );
  }

  public marketingSeriesDetailPageV2(
    contentGuid: string,
    platform: PlatformApiModel,
    userSegment: UserSegmentApiModel,
    countryCode: string
  ): Observable<MarketingDetailPageItemEntity> {
    const id = `series${contentGuid}:${countryCode}`;
    // @ts-ignore
    return this.get(
      id,
      this.tables.detailPageItem,
      // @ts-ignore
      this.detailPageDataService.marketingSeriesDetailPageV2(contentGuid, platform, userSegment, countryCode)
    );
  }

  public getListOfAllSellableSubscriptionPackages$(
    locale: string,
    territory: string,
    subscriptionKey: string,
    endpointUrl: string
  ): Observable<SellableSubscriptionsEntity> {
    const id = 'SELLABLE-PACKAGES';
    const httpOptions = {
      headers: new HttpHeaders({
        'Ocp-Apim-Subscription-Key': subscriptionKey,
      }),
      params: new HttpParams({
        fromObject: {
          locale,
          territory: territory.toUpperCase(),
        },
      }),
    };

    return this.get(id, this.tables.sellableSubscriptions, this.httpClient.get<SellableSubscriptionsEntity>(endpointUrl, httpOptions));
  }
}
